import { z } from 'zod';
import { baseMessageSchema, createMessageId } from './_base';

export enum BeaconMessageType {
  BEACON_SHARE_CODE_CREATE = 'BEACON_SHARE_CODE_CREATE',
  BEACON_SHARE_CODE_CREATED = 'BEACON_SHARE_CODE_CREATED',
  BEACON_SHARE_CODE_RECLAIM = 'BEACON_SHARE_CODE_RECLAIM',
  BEACON_SHARE_CODE_RECLAIMED = 'BEACON_SHARE_CODE_RECLAIMED',
  BEACON_SHARE_CODE_RECLAIM_FAILED = 'BEACON_SHARE_CODE_RECLAIM_FAILED',
  BEACON_SHARE_CODE_END = 'BEACON_SHARE_CODE_END',
  BEACON_SHARE_CODE_ENDED = 'BEACON_SHARE_CODE_ENDED',
  BEACON_SHARE_CODE_RESOLVE = 'BEACON_SHARE_CODE_RESOLVE',
  BEACON_SHARE_CODE_RESOLVED = 'BEACON_SHARE_CODE_RESOLVED',
  BEACON_SHARE_CODE_RESOLVE_ERROR = 'BEACON_SHARE_CODE_RESOLVE_ERROR',
  BEACON_PLAYER_COUNT_UPDATED = 'BEACON_PLAYER_COUNT_UPDATED',
}

export enum BeaconResolveErrorCode {
  NOT_FOUND = 'NOT_FOUND',
  FULL = 'FULL',
}

// ========================================
// Payload Schemas
// ========================================

const beaconShareCodeCreatePayloadSchema = z.object({
  machineId: z.string(),
  authId: z.string(),
  maxPlayers: z.number(),
  streamTitle: z.string().optional(),
});

const beaconShareCodeCreatedPayloadSchema = z.object({
  shareCode: z.string(),
  machineId: z.string(),
  maxPlayers: z.number(),
  streamTitle: z.string().optional(),
});

const beaconShareCodeReclaimPayloadSchema = z.object({
  shareCode: z.string(),
  machineId: z.string(),
  authId: z.string(),
});

const beaconShareCodeReclaimedPayloadSchema = z.object({
  shareCode: z.string(),
});

const beaconShareCodeReclaimFailedPayloadSchema = z.object({
  reason: z.string(),
});

const beaconShareCodeEndPayloadSchema = z.object({
  shareCode: z.string(),
});

const beaconShareCodeEndedPayloadSchema = z.object({
  acknowledged: z.boolean(),
});

const beaconShareCodeResolvePayloadSchema = z.object({
  shareCode: z.string(),
});

const beaconShareCodeResolvedPayloadSchema = z.object({
  shareCode: z.string(),
  hostPeerId: z.string(),
  maxPlayers: z.number(),
  currentPlayers: z.number(),
  streamTitle: z.string().optional(),
});

const beaconShareCodeResolveErrorPayloadSchema = z.object({
  shareCode: z.string(),
  code: z.nativeEnum(BeaconResolveErrorCode),
  message: z.string(),
});

const beaconPlayerCountUpdatedPayloadSchema = z.object({
  shareCode: z.string(),
  currentPlayers: z.number(),
  maxPlayers: z.number(),
});

// ========================================
// Full Message Schemas
// ========================================

const beaconShareCodeCreateSchema = baseMessageSchema
  .extend({ type: z.literal(BeaconMessageType.BEACON_SHARE_CODE_CREATE) })
  .merge(beaconShareCodeCreatePayloadSchema);

const beaconShareCodeCreatedSchema = baseMessageSchema
  .extend({ type: z.literal(BeaconMessageType.BEACON_SHARE_CODE_CREATED) })
  .merge(beaconShareCodeCreatedPayloadSchema);

const beaconShareCodeReclaimSchema = baseMessageSchema
  .extend({ type: z.literal(BeaconMessageType.BEACON_SHARE_CODE_RECLAIM) })
  .merge(beaconShareCodeReclaimPayloadSchema);

const beaconShareCodeReclaimedSchema = baseMessageSchema
  .extend({ type: z.literal(BeaconMessageType.BEACON_SHARE_CODE_RECLAIMED) })
  .merge(beaconShareCodeReclaimedPayloadSchema);

const beaconShareCodeReclaimFailedSchema = baseMessageSchema
  .extend({ type: z.literal(BeaconMessageType.BEACON_SHARE_CODE_RECLAIM_FAILED) })
  .merge(beaconShareCodeReclaimFailedPayloadSchema);

const beaconShareCodeEndSchema = baseMessageSchema
  .extend({ type: z.literal(BeaconMessageType.BEACON_SHARE_CODE_END) })
  .merge(beaconShareCodeEndPayloadSchema);

const beaconShareCodeEndedSchema = baseMessageSchema
  .extend({ type: z.literal(BeaconMessageType.BEACON_SHARE_CODE_ENDED) })
  .merge(beaconShareCodeEndedPayloadSchema);

const beaconShareCodeResolveSchema = baseMessageSchema
  .extend({ type: z.literal(BeaconMessageType.BEACON_SHARE_CODE_RESOLVE) })
  .merge(beaconShareCodeResolvePayloadSchema);

const beaconShareCodeResolvedSchema = baseMessageSchema
  .extend({ type: z.literal(BeaconMessageType.BEACON_SHARE_CODE_RESOLVED) })
  .merge(beaconShareCodeResolvedPayloadSchema);

const beaconShareCodeResolveErrorSchema = baseMessageSchema
  .extend({ type: z.literal(BeaconMessageType.BEACON_SHARE_CODE_RESOLVE_ERROR) })
  .merge(beaconShareCodeResolveErrorPayloadSchema);

const beaconPlayerCountUpdatedSchema = baseMessageSchema
  .extend({ type: z.literal(BeaconMessageType.BEACON_PLAYER_COUNT_UPDATED) })
  .merge(beaconPlayerCountUpdatedPayloadSchema);

// ========================================
// Discriminated Union
// ========================================

export const beaconMessageSchema = z.discriminatedUnion('type', [
  beaconShareCodeCreateSchema,
  beaconShareCodeCreatedSchema,
  beaconShareCodeReclaimSchema,
  beaconShareCodeReclaimedSchema,
  beaconShareCodeReclaimFailedSchema,
  beaconShareCodeEndSchema,
  beaconShareCodeEndedSchema,
  beaconShareCodeResolveSchema,
  beaconShareCodeResolvedSchema,
  beaconShareCodeResolveErrorSchema,
  beaconPlayerCountUpdatedSchema,
]);

// ========================================
// Exported Payload Types
// ========================================

export type BeaconShareCodeCreatePayload = z.infer<typeof beaconShareCodeCreatePayloadSchema>;
export type BeaconShareCodeCreatedPayload = z.infer<typeof beaconShareCodeCreatedPayloadSchema>;
export type BeaconShareCodeReclaimPayload = z.infer<typeof beaconShareCodeReclaimPayloadSchema>;
export type BeaconShareCodeReclaimedPayload = z.infer<typeof beaconShareCodeReclaimedPayloadSchema>;
export type BeaconShareCodeReclaimFailedPayload = z.infer<typeof beaconShareCodeReclaimFailedPayloadSchema>;
export type BeaconShareCodeEndPayload = z.infer<typeof beaconShareCodeEndPayloadSchema>;
export type BeaconShareCodeEndedPayload = z.infer<typeof beaconShareCodeEndedPayloadSchema>;
export type BeaconShareCodeResolvePayload = z.infer<typeof beaconShareCodeResolvePayloadSchema>;
export type BeaconShareCodeResolvedPayload = z.infer<typeof beaconShareCodeResolvedPayloadSchema>;
export type BeaconShareCodeResolveErrorPayload = z.infer<typeof beaconShareCodeResolveErrorPayloadSchema>;
export type BeaconPlayerCountUpdatedPayload = z.infer<typeof beaconPlayerCountUpdatedPayloadSchema>;

export type BeaconMessage = z.infer<typeof beaconMessageSchema>;

// ========================================
// Payload Type Mapping
// ========================================

type PayloadType<T extends BeaconMessageType> =
  T extends BeaconMessageType.BEACON_SHARE_CODE_CREATE ? BeaconShareCodeCreatePayload
  : T extends BeaconMessageType.BEACON_SHARE_CODE_CREATED ? BeaconShareCodeCreatedPayload
  : T extends BeaconMessageType.BEACON_SHARE_CODE_RECLAIM ? BeaconShareCodeReclaimPayload
  : T extends BeaconMessageType.BEACON_SHARE_CODE_RECLAIMED ? BeaconShareCodeReclaimedPayload
  : T extends BeaconMessageType.BEACON_SHARE_CODE_RECLAIM_FAILED ? BeaconShareCodeReclaimFailedPayload
  : T extends BeaconMessageType.BEACON_SHARE_CODE_END ? BeaconShareCodeEndPayload
  : T extends BeaconMessageType.BEACON_SHARE_CODE_ENDED ? BeaconShareCodeEndedPayload
  : T extends BeaconMessageType.BEACON_SHARE_CODE_RESOLVE ? BeaconShareCodeResolvePayload
  : T extends BeaconMessageType.BEACON_SHARE_CODE_RESOLVED ? BeaconShareCodeResolvedPayload
  : T extends BeaconMessageType.BEACON_SHARE_CODE_RESOLVE_ERROR ? BeaconShareCodeResolveErrorPayload
  : T extends BeaconMessageType.BEACON_PLAYER_COUNT_UPDATED ? BeaconPlayerCountUpdatedPayload
  : never;

// ========================================
// Generator
// ========================================

export function generateBeaconMessage<T extends BeaconMessageType>(
  type: T,
  payload: PayloadType<T>,
): BeaconMessage {
  const base = {
    id: createMessageId(),
    timestamp: Date.now(),
    type,
  };

  switch (type) {
    case BeaconMessageType.BEACON_SHARE_CODE_CREATE:
      return beaconShareCodeCreateSchema.parse({ ...base, ...payload });
    case BeaconMessageType.BEACON_SHARE_CODE_CREATED:
      return beaconShareCodeCreatedSchema.parse({ ...base, ...payload });
    case BeaconMessageType.BEACON_SHARE_CODE_RECLAIM:
      return beaconShareCodeReclaimSchema.parse({ ...base, ...payload });
    case BeaconMessageType.BEACON_SHARE_CODE_RECLAIMED:
      return beaconShareCodeReclaimedSchema.parse({ ...base, ...payload });
    case BeaconMessageType.BEACON_SHARE_CODE_RECLAIM_FAILED:
      return beaconShareCodeReclaimFailedSchema.parse({ ...base, ...payload });
    case BeaconMessageType.BEACON_SHARE_CODE_END:
      return beaconShareCodeEndSchema.parse({ ...base, ...payload });
    case BeaconMessageType.BEACON_SHARE_CODE_ENDED:
      return beaconShareCodeEndedSchema.parse({ ...base, ...payload });
    case BeaconMessageType.BEACON_SHARE_CODE_RESOLVE:
      return beaconShareCodeResolveSchema.parse({ ...base, ...payload });
    case BeaconMessageType.BEACON_SHARE_CODE_RESOLVED:
      return beaconShareCodeResolvedSchema.parse({ ...base, ...payload });
    case BeaconMessageType.BEACON_SHARE_CODE_RESOLVE_ERROR:
      return beaconShareCodeResolveErrorSchema.parse({ ...base, ...payload });
    case BeaconMessageType.BEACON_PLAYER_COUNT_UPDATED:
      return beaconPlayerCountUpdatedSchema.parse({ ...base, ...payload });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled beacon message type: ${exhaustiveCheck}`);
    }
  }
}
